import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useSession } from '@dw/hooks';
import { useSignOutMutation } from '@dw/hooks/api/AuthApi';

/**
 * Signs the user out end to end: revokes the refresh token's family server side, then clears the
 * local session and the query cache. It is split this way because the session provider sits above
 * the HTTP client and cannot call the API itself.
 */
export const useSignOut = () => {

  const { getAccessToken, getRefreshToken, signOut: clearSession } = useSession();
  const { mutateAsync: revokeSession, isPending: isSigningOut } = useSignOutMutation();
  const queryClient = useQueryClient();

  const signOut = useCallback(async (): Promise<void> => {
    try {
      // Settle any refresh already in flight FIRST. The request itself would have done this, but by
      // then the token read below is the pre-rotation one — and revoking a token the rotation has
      // already replaced fails silently, leaving the live family alive on the server.
      await getAccessToken();

      await revokeSession({ refreshToken: getRefreshToken() ?? undefined });
    } catch {
      // A failed revoke must not strand the user in a signed-in shell. The local session goes either
      // way, and the refresh token expires on its own.
    }

    clearSession();
    // Unconditional, and here rather than in the mutation: a failed revoke still ends the session
    // locally, and signing out from the onboarding screens never remounts the login page, so nothing
    // else would drop the previous user's cached queries before the next sign-in.
    queryClient.clear();
  }, [getAccessToken, revokeSession, getRefreshToken, clearSession, queryClient]);

  return { signOut, isSigningOut };
}
